import { Box, Stack, Typography, Chip } from '@mui/material';
import React from 'react';

interface QuestionAnswer {
  question: string;
  answer: string;
  question_type: string;
  required: boolean;
}

interface CustomerRequirements {
  questions_and_answers: QuestionAnswer[];
  raw_responses: Record<string, string>;
  emergency_service: boolean;
  service_type: string;
}

interface CustomerRequirementsProps {
  requirements: string;
}

const CustomerRequirementsDisplay: React.FC<CustomerRequirementsProps> = ({ requirements }) => {
  let parsedRequirements: CustomerRequirements;
  
  try {
    parsedRequirements = JSON.parse(requirements);
  } catch (error) {
    // Fallback to display raw text if JSON parsing fails
    return (
      <Box>
        <Typography variant="caption" color="text.secondary">
          Customer Requirements
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {requirements}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        Customer Requirements
      </Typography>
      
      <Stack spacing={1.5} sx={{ mt: 1 }}>
        {/* Service Information */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip
            label={`Service: ${parsedRequirements.service_type}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.75rem' }}
          />
          {parsedRequirements.emergency_service && (
            <Chip
              label="Emergency Service"
              size="small"
              color="error"
              sx={{ fontSize: '0.75rem' }}
            />
          )}
        </Stack>

        {/* Questions and Answers */}
        {parsedRequirements.questions_and_answers && 
         parsedRequirements.questions_and_answers.length > 0 && (
          <Stack spacing={1}>
            {parsedRequirements.questions_and_answers.map((qa) => (
              <Box 
                key={`${qa.question.slice(0, 20)}-${qa.answer.slice(0, 20)}`} 
                sx={{ 
                  p: 1.5, 
                  bgcolor: 'background.paper', 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ fontWeight: 500, mb: 0.5, color: 'text.primary' }}
                    >
                      {qa.question}
                      {qa.required && (
                        <Typography 
                          component="span" 
                          sx={{ color: 'error.main', ml: 0.5 }}
                        >
                          *
                        </Typography>
                      )}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                    >
                      {qa.answer}
                    </Typography>
                  </Box>
                  <Chip
                    label={qa.question_type}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.7rem',
                      height: 20,
                      ml: 1,
                      flexShrink: 0
                    }}
                  />
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default CustomerRequirementsDisplay;